(() => {
  if (customElements.get('geosort-dock')) return;
  class GeosortDock extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.dock = this.querySelector('.prysm-dock-container');
      this.rail = this.querySelector('.prysm-dock-rail');
      this.expansion = this.querySelector('.prysm-dock-expansion');
      this.mobileActions = this.querySelector('.prysm-dock-mobile-actions');
      this.items = [...this.querySelectorAll('[data-dock-item]')];
      this.mobile = matchMedia('(max-width: 768px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.hover = matchMedia('(hover: hover)');
      this.activePanel = null;
      this.activeTrigger = null;
      this.clickOpenedTrigger = null;
      this.heroes = [];
      this.heroObserver = new ResizeObserver(() => this.queueVisibility());
      const on = (target, name, callback, options = {}) => target.addEventListener(name, callback, { ...options, signal: this.controller.signal });

      this.items.forEach(item => {
        on(item, 'pointerenter', () => {
          clearTimeout(this.closeTimer);
          clearTimeout(this.openTimer);
          if (this.mobile.matches || !this.hover.matches) return;
          this.openTimer = setTimeout(() => {
            const panel = this.panelFor(item);
            if (panel) this.open(item, panel);
            else this.close(false);
          }, 100);
        });
        on(item, 'pointerleave', () => clearTimeout(this.openTimer));
        on(item, 'click', event => {
          const panel = this.panelFor(item);
          if (!panel) return;
          const destination = item.getAttribute('href');
          const canNavigate = !this.mobile.matches && destination && destination !== '#';
          if (canNavigate && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
          clearTimeout(this.openTimer);
          // Hover previews the menu; only an actual first click arms navigation.
          if (canNavigate && this.activePanel === panel && this.clickOpenedTrigger === item) return;
          event.preventDefault();
          if (canNavigate) {
            this.open(item, panel);
            this.clickOpenedTrigger = item;
            return;
          }
          if (this.activePanel === panel || (this.mobile.matches && item.hasAttribute('data-dock-mobile-trigger') && this.activePanel)) this.close(false);
          else this.open(item, panel);
        });
        on(item, 'keydown', event => {
          if (event.key !== 'ArrowDown') return;
          const panel = this.panelFor(item);
          if (!panel) return;
          event.preventDefault();
          this.open(item, panel);
          requestAnimationFrame(() => requestAnimationFrame(() => {
            if (this.activePanel === panel) (panel.querySelector('a') || panel.querySelector('button'))?.focus({ preventScroll: true });
          }));
        });
      });
      on(this.dock, 'pointerenter', () => clearTimeout(this.closeTimer));
      on(this.dock, 'pointerleave', () => {
        clearTimeout(this.openTimer);
        if (!this.mobile.matches && this.hover.matches) this.closeTimer = setTimeout(() => this.close(false), 240);
      });
      on(this, 'click', event => {
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const close = event.target.closest('[data-dock-close]');
        if (close) this.close(true);
        const toggle = event.target.closest('.prysm-dock-group-toggle');
        if (toggle) this.toggleGroup(toggle);
        const link = event.target.closest('a[href]');
        if (!link) return;
        this.close(false);
        const url = new URL(link.href, location.href);
        if (url.origin === location.origin && url.pathname.replace(/\/$/, '') === location.pathname.replace(/\/$/, '') && url.hash && url.hash !== '#open-chat') {
          let target;
          try { target = document.getElementById(decodeURIComponent(url.hash.slice(1))); } catch { return; }
          if (!target) return;
          event.preventDefault();
          history.pushState(null, '', url.hash);
          target.scrollIntoView({ behavior: this.reduced.matches ? 'auto' : 'smooth', block: 'start' });
        }
      });
      on(document, 'pointerdown', event => {
        if (!this.contains(event.target)) this.close(false);
      });
      on(document, 'keydown', event => {
        if (event.key === 'Escape' && this.activePanel) {
          event.preventDefault();
          this.close(true);
        }
      });
      on(this, 'focusout', () => {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close(false);
        }, 0);
      });
      on(window, 'scroll', () => this.queueVisibility(), { passive: true });
      on(window, 'load', () => this.refreshHeroes());
      on(window, 'pageshow', () => this.refreshHeroes());
      on(document, 'DOMContentLoaded', () => this.refreshHeroes());
      on(window, 'resize', () => { this.measure(); this.queueVisibility(); }, { passive: true });
      if (window.visualViewport) on(visualViewport, 'resize', () => { this.measure(); this.queueVisibility(); }, { passive: true });
      on(this.mobile, 'change', () => {
        this.close(false);
        this.measure();
        this.syncMenuIcon(true);
        this.queueVisibility();
      });
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(name => on(document, name, () => {
        cancelAnimationFrame(this.heroFrame);
        this.heroFrame = requestAnimationFrame(() => this.refreshHeroes());
      }));
      on(document, 'visibilitychange', () => { if (document.hidden) this.close(false); });
      on(document, 'shopify:block:select', event => {
        if (!this.contains(event.target) && !event.detail?.blockId) return;
        const id = event.detail?.blockId;
        const item = this.items.find(el => el.dataset.dockItem === id) || this.items.find(el => {
          const panel = this.panelFor(el);
          return panel?.querySelector(`[data-dock-menu-entry="${CSS.escape(id || '')}"]`);
        });
        const panel = item && this.panelFor(item);
        if (panel) this.open(item, panel);
      });
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.rail);
      this.querySelectorAll('.prysm-dock-panel-content').forEach(content => this.resizeObserver.observe(content));
      this.pageObserver = new ResizeObserver(() => this.queueVisibility());
      this.pageObserver.observe(document.body);
      this.items.filter(item => item.hasAttribute('data-dock-mobile-trigger')).forEach(item => {
        const icon = item.querySelector('lord-icon');
        if (!icon) return;
        // The menu state drives the morph, including Escape and outside-click closure.
        icon.removeAttribute('trigger');
        on(icon, 'ready', () => this.syncMenuIcon(true));
      });
      this.querySelectorAll('.prysm-dock-icon:not([data-dock-mobile-trigger]) lord-icon, .prysm-dock-group-icon lord-icon').forEach(icon => {
        const control = icon.closest('button, a');
        if (!control) return;
        icon.removeAttribute('trigger');
        const play = () => {
          const player = icon.playerInstance;
          if (!this.reduced.matches && (player?.isReady ?? player?.ready) && !(player.isPlaying ?? player.playing)) {
            (player.playFromBeginning || player.playFromStart).call(player);
          }
        };
        on(control, 'pointerenter', event => { if (event.pointerType === 'mouse') play(); });
        on(control, 'click', play);
      });
      this.updateCurrentLinks();
      this.measure();
      this.refreshHeroes();
      this.syncMenuIcon(true);
    }

    panelFor(item) {
      if (this.mobile.matches && item.hasAttribute('data-dock-mobile-trigger')) return this.querySelector('.prysm-dock-mobile-panel');
      return item.dataset.dockPanel ? document.getElementById(item.dataset.dockPanel) : null;
    }

    open(trigger, panel) {
      clearTimeout(this.closeTimer);
      clearTimeout(this.hideTimer);
      if (this.activePanel === panel) return;
      this.clickOpenedTrigger = null;
      this.activeTrigger?.setAttribute('aria-expanded', 'false');
      this.querySelectorAll('.prysm-dock-panel').forEach(el => { el.hidden = el !== panel; });
      this.activePanel = panel;
      this.activeTrigger = trigger;
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-expanded', 'true');
      this.expansion.inert = false;
      this.expansion.setAttribute('aria-hidden', 'false');
      this.classList.add('is-expanded');
      this.syncMenuIcon();
      panel.scrollTop = 0;
      this.measure();
      if (!this.reduced.matches) {
        this.panelAnimation?.cancel();
        this.panelAnimation = panel.animate([{ opacity: 0, transform: 'translateY(-5px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 300, easing: 'cubic-bezier(.22,1,.36,1)' });
      }
      this.updateVisibility();
    }

    close(restoreFocus, updateVisibility = true) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.clickOpenedTrigger = null;
      if (!this.activePanel) return;
      const trigger = this.activeTrigger;
      this.activePanel = null;
      this.activeTrigger = null;
      trigger?.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-expanded');
      this.syncMenuIcon();
      this.expansion.inert = true;
      this.expansion.setAttribute('aria-hidden', 'true');
      this.measure();
      this.hideTimer = setTimeout(() => this.querySelectorAll('.prysm-dock-panel').forEach(el => { el.hidden = true; }), this.reduced.matches ? 0 : 500);
      if (restoreFocus) trigger?.focus({ preventScroll: true });
      if (updateVisibility) this.updateVisibility();
    }

    toggleGroup(toggle) {
      const expanded = toggle.getAttribute('aria-expanded') !== 'true';
      this.querySelectorAll('.prysm-dock-group-toggle').forEach(other => {
        const show = other === toggle && expanded;
        other.setAttribute('aria-expanded', String(show));
        other.nextElementSibling.inert = !show;
      });
    }

    syncMenuIcon(immediate = false) {
      const expanded = this.mobile.matches && !!this.activePanel;
      this.items.filter(item => item.hasAttribute('data-dock-mobile-trigger')).forEach(item => {
        if (this.mobile.matches) {
          item.setAttribute('aria-expanded', String(expanded));
          item.setAttribute('aria-label', expanded ? (this.dataset.closeLabel || 'Close menu') : (this.dataset.openLabel || 'Open menu'));
        }
        const player = item.querySelector('lord-icon')?.playerInstance;
        if (!(player?.isReady ?? player?.ready)) return;
        player.direction = expanded ? 1 : -1;
        if (immediate || this.reduced.matches) {
          player.pause();
          if (expanded) (player.goToLastFrame || player.seekToEnd).call(player);
          else (player.goToFirstFrame || player.seekToStart).call(player);
        } else {
          player.play();
        }
      });
    }

    measure() {
      if (!this.isConnected) return;
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.visualViewport?.height || innerHeight;
      const railHeight = this.rail.offsetHeight;
      const closedWidth = Math.ceil(this.rail.scrollWidth + 2);
      const gutter = parseFloat(getComputedStyle(this).getPropertyValue('--dock-mobile-gutter')) || 16;
      const width = this.mobile.matches ? viewportWidth - gutter * 2 : Math.min(closedWidth, viewportWidth - 24);
      this.style.setProperty('--dock-width', `${width}px`);
      this.style.setProperty('--dock-rail-height', `${railHeight}px`);
      const wrapper = getComputedStyle(this);
      const position = this.mobile.matches ? this.dataset.mobilePosition : this.dataset.desktopPosition;
      const offset = parseFloat(position === 'bottom' ? wrapper.bottom : wrapper.top) || 0;
      const available = Math.max(100, viewportHeight - offset - railHeight - 18);
      const actionsHeight = this.mobile.matches ? this.mobileActions.offsetHeight : 0;
      this.style.setProperty('--dock-available-height', `${Math.max(0, available - actionsHeight)}px`);
      let panelHeight = 0;
      if (this.activePanel) {
        const panelStyle = getComputedStyle(this.activePanel);
        // Measure natural content so closing an accordion can shrink the dock too.
        const naturalHeight = this.activePanel.firstElementChild.getBoundingClientRect().height + parseFloat(panelStyle.paddingTop) + parseFloat(panelStyle.paddingBottom);
        panelHeight = Math.min(naturalHeight, available - actionsHeight) + actionsHeight;
      }
      this.style.setProperty('--dock-panel-height', `${panelHeight}px`);
      this.style.setProperty('--dock-height', `${railHeight + panelHeight + 2}px`);
    }

    refreshHeroes() {
      this.heroObserver.disconnect();
      this.heroes = [...document.querySelectorAll('main [data-dock-hero]')];
      this.heroes.forEach(hero => this.heroObserver.observe(hero));
      this.updateVisibility();
    }

    queueVisibility() {
      if (this.visibilityFrame) return;
      this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = null;
        if (this.isConnected) this.updateVisibility();
      });
    }

    shouldReveal() {
      if (this.dataset.revealMode === 'always') return true;
      const heroes = this.heroes.map(hero => hero.getBoundingClientRect()).filter(rect => rect.width > 0 && rect.height > 0);
      if (!heroes.length) return true;
      // The opening hero, not page length, determines when navigation begins.
      if (heroes[0].bottom > 0) return false;
      const position = this.mobile.matches ? this.dataset.mobilePosition : this.dataset.desktopPosition;
      const style = getComputedStyle(this);
      const offset = parseFloat(position === 'bottom' ? style.bottom : style.top) || 0;
      const height = this.rail.offsetHeight + 2;
      const viewportHeight = window.visualViewport?.height || innerHeight;
      const top = position === 'bottom' ? viewportHeight - offset - height : offset;
      // Repeated hero sections also stay clear of the dock's collapsed footprint.
      return !heroes.slice(1).some(rect => rect.top < top + height && rect.bottom > top);
    }

    updateVisibility() {
      const visible = this.shouldReveal();
      if (!visible && this.activePanel) this.close(false, false);
      this.classList.toggle('is-visible', visible);
      this.classList.toggle('is-hidden', !visible);
      this.inert = !visible;
    }

    updateCurrentLinks() {
      const path = location.pathname.replace(/\/$/, '') || '/';
      this.querySelectorAll('a[href]').forEach(link => {
        const url = new URL(link.href, location.href);
        if (url.origin === location.origin && (url.pathname.replace(/\/$/, '') || '/') === path && !url.hash) link.setAttribute('aria-current', 'page');
      });
      this.items.forEach(item => {
        const href = item.dataset.prysmDockLink;
        if (!href) return;
        const url = new URL(href, location.href);
        if (url.origin === location.origin && (url.pathname.replace(/\/$/, '') || '/') === path && !url.hash) item.classList.add('is-current');
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.pageObserver?.disconnect();
      this.heroObserver?.disconnect();
      cancelAnimationFrame(this.visibilityFrame);
      cancelAnimationFrame(this.heroFrame);
      this.visibilityFrame = null;
      this.panelAnimation?.cancel();
      ['openTimer', 'closeTimer', 'hideTimer', 'focusTimer'].forEach(key => clearTimeout(this[key]));
      this.controller = null;
    }
  }
  customElements.define('geosort-dock', GeosortDock);
})();
